import React, { ReactElement, ReactNode } from 'react';
import {
    useQuery,
    QueryOptions,
    useMutation,
    MutationOptions,
    QueryClient,
    QueryClientProvider,
    useQueryClient
} from 'react-query';
import { Middleware, Options, fetchGraphQL } from "./graphql-fetch.util";
import { DocumentNode } from "graphql";

export interface FetchGraphQL<TData, TVariables> {
    (
      query: string | DocumentNode,
      variables?: TVariables,
      fetchMiddleware?: Middleware[]
    ): Promise<TData>;
  }
  
export interface GraphQLConfig {
    uri: string;
    options?: Options;
    middleware?: Middleware[];
}

interface ProviderProps extends GraphQLConfig {
    children: ReactNode
}

function useFetchGraphQL ({
    uri,
    options = {},
    middleware = []
}: GraphQLConfig) {
    const fetch = React.useCallback(
        async function<TData, TVariables extends object>(
            query: string,
            variables?: TVariables,
            fetchMiddleware: Middleware[] = []
        ): Promise<TData> {
            return await fetchGraphQL(uri, query, variables, {
                ...options,
                middleware: [...fetchMiddleware, ...middleware]
            });
        },
        [uri, options, middleware]
    );

    return fetch;
}

const GraphQLContext = React.createContext(undefined);

export function useGraphQL<TData, TVariables>() {
    return (React.useContext(GraphQLContext) as unknown) as FetchGraphQL<TData, TVariables>;
}

const queryClient = new QueryClient();

export const useGraphQLQueryClient = () => {
    return useQueryClient();
}

export const GraphQLProvider = ({ children, ...config }: ProviderProps) : ReactElement => {
    const fetch = useFetchGraphQL(config);
    return (
        <GraphQLContext.Provider value={fetch as any}>
            <QueryClientProvider client={queryClient}>
                { children }
            </QueryClientProvider>
        </GraphQLContext.Provider>
    );
}

interface GraphQLVariables<TVariables> {
    variables?: TVariables;
}

export interface UseQueryOptions<TData, TVariables> extends QueryOptions<TData>, GraphQLVariables<TVariables> {
    middleware?: Middleware[];
    skip?: boolean;
    opName?: string;
}

export interface UseQueryResult<TData, TError, TBoolean extends boolean> {
    // TODO: Update based on documentation
    data: TData | undefined,
    error: TError | null,
    isLoading: TBoolean,
}

export const getDocumentKey = (query: DocumentNode): string => {
    return (query.definitions[0] as any).name.value;
}

export function useGraphQLQuery<TData, TVariables extends object, TError>(
    query: string | DocumentNode,
    {
        variables = {} as TVariables,
        middleware = [],
        opName = typeof query === 'string' ? query : getDocumentKey(query),
        skip = false, // For lazy evaluations
        ...options
    }: UseQueryOptions<TData, TVariables> = {}
): UseQueryResult<TData, TError, boolean> {
    const fetchGraphQL = useGraphQL();
    const key: any = [opName, ...(skip ? [false] : [{ variables }])];
    const { data, error, isLoading, ...queryObject } = useQuery<TData, TError> (
        key, 
        (async ({ variables }: GraphQLVariables<TVariables>) => {
            return await fetchGraphQL(query, variables, middleware);
        }) as any,
        {
            refetchOnMount: true,
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
            cacheTime: 0,
        }
    );

    return {
        data,
        error,
        isLoading,
        ...queryObject
    };
}

export interface UseMutationOptions<TData, TVariables> extends MutationOptions<TData, TVariables> {
    middleware?: Middleware[];
    opName?: string;
}

export interface UseMutationResult<TData, TError> {
    // TODO: Update based on documentation
    data: TData | undefined,
    error: TError | null,
    isLoading: boolean
}

export function useGraphQLMutation<TData, TVariables extends object, TError> (
    mutation: string | DocumentNode,
    {
        middleware = [],
        opName = typeof mutation === 'string' ? mutation : getDocumentKey(mutation),
        ...options
    }: UseMutationOptions<TData, TVariables> = {}
): UseMutationResult<TData, TError> {
    const fetchGraphQL = useGraphQL();
    const { data, error, isLoading, ...mutationObject } = useMutation<TData, TError>((async (variables: TVariables) => {
        return await fetchGraphQL(mutation, variables, middleware);
    }) as any);

    return {
        data, 
        error,
        isLoading,
        ...mutationObject
    };
}

// TODO: Add utilities to directly interact with documentation
// such as preFetch, add subscriptions, query observers etc.
// Based on api design and requirements.